import * as React from "react";

import {ICON_MIGRATION, Icon as IconLiterals} from "@client/constants";

import {Highlight} from "@client/components/Highlight";
import {classNames, server} from "@reactivated";
import * as icons from "@reactivated/icons";

const styles = {
    wrapper: "relative",
    linearIconsFill: "linear-icons-fill",
    icon: "shadow-[0_0_0_0_rgba(0,0,0,1)] align-middle h-3.5 w-3.5 block",
    small: "h-2.5 w-2.5",
    medium: "h-3 w-3",
    teenager: "h-4 w-4",
    large: "h-5 w-5",
    xl: "h-[26px] w-[26px]",
    relative: "h-[1.5em] w-[1.5em]",
    pulse: "animate-[spin_0.75s_steps(32)_infinite]",
    iconWithContent: "flex flex-row items-center text-left",
    iconWithMinimalSpace: "icon-minimal-space",
    iconWithContentSpace: "icon-content-space",
    right: "flex-row-reverse",
    iconArea: "",
    contentArea: "shrink",
} as const;

export type IconType = IconLiterals;

interface BaseIconProps {
    icon: IconType | "empty";
    pulse?: boolean;
    highlight?: boolean;
    large?: boolean | "relative" | "xl" | "small" | "medium" | "teenager";
    className?: string;
}

interface BareIconProps extends BaseIconProps {
    children?: never;
    align?: never;
    minimalSpace?: never;
}

interface IconWithContent extends BaseIconProps {
    children: React.ReactNode;
    align?: "left" | "right";
    minimalSpace?: true;
}

type Props = BareIconProps | IconWithContent;

interface SimpleIconProps {
    icon: IconType;
    className?: string;
}

const resolveIcon = (icon: IconType) => {
    const isMacLike =
        typeof window !== "undefined" &&
        /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
    if (icon === "share" && isMacLike === true) {
        return "custom-apple-share" as const;
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return ICON_MIGRATION[icon as keyof typeof ICON_MIGRATION] ?? icon;
};

export const SimpleIcon = (props: SimpleIconProps) => {
    if (props.icon == "icons-go-here") {
        throw new Error("Not used yet");
    }

    const newIcon = resolveIcon(props.icon);

    const component = server.core.ICON_MAP[newIcon];

    const IconComponent = icons[component];

    // Useful for XPATH selection
    const classes = classNames(
        props.className,
        `icon-${newIcon}`,
        styles.linearIconsFill,
    );

    return <IconComponent title={props.icon} className={classes} />;
};

export const Icon = (props: Props) => {
    const iconClasses = classNames(
        styles.icon,
        props.large === true && styles.large,
        props.large === "relative" && styles.relative,
        props.large === "xl" && styles.xl,
        props.large === "medium" && styles.medium,
        props.large === "small" && styles.small,
        props.large === "teenager" && styles.teenager,
        props.pulse === true && styles.pulse,
        props.className,
    );

    const icon = (
        <div className={styles.wrapper}>
            {props.highlight === true ? <Highlight /> : null}
            {props.icon == "empty" ? (
                <svg className={iconClasses}>empty</svg>
            ) : (
                <SimpleIcon icon={props.icon} className={iconClasses} />
            )}
        </div>
    );

    if (props.children != null) {
        const classes = classNames(
            styles.iconWithContent,
            props.align === "right" && styles.right,
            props.minimalSpace === true
                ? styles.iconWithMinimalSpace
                : styles.iconWithContentSpace,
        );
        return (
            <div className={classes}>
                <div className={styles.iconArea}>{icon}</div>{" "}
                <div className={styles.contentArea}>{props.children}</div>
            </div>
        );
    }
    return icon;
};
